// Motion, read back out of the stylesheet (dioxus-plan.md D285).
//
// Everything in the window moves on one curve and one tempo: `--motion-easing`
// and `--motion-duration` in `app.css`, which the desktop sets on `.window` from
// the reader's own settings and the web leaves at their `:root` defaults. The
// stylesheet spends them on every transition and every arrival or departure.
// What it cannot reach is a movement a script makes by hand — the feed's
// arrival and its short landings animate `scrollTop`, which no transition
// can — and a script that kept its own copies of the two numbers would glide
// on a curve of its own at a tempo of its own, and go on gliding after the
// reader had turned motion off. So it asks the element instead:
//
//     const m = window.__echoMotion(el);
//     m.ms       // the tempo el inherits, in milliseconds — 0 while motion is off
//     m.ease(p)  // progress along the curve at the time fraction p, 0..1
//
// Once per movement, not per frame: a computed-style read inside a frame
// callback is a style recalculation per frame.
//
// And the other way round: the stylesheet asking the page. Nothing in motion
// casts a shadow, and a shadow that went out comes back in over one more beat
// once the motion is over (D291, D292, «Transitions» in `app.css`). The
// stylesheet can say neither about a transition — no selector matches a box
// in the middle of one, and half of what moves by transition moves by numbers
// written inline (the ink's `--ink-*`, a stream rail's `translateY`) — nor
// about the end of an arrival: a finished animation hands its values back
// without a transition, which is a snap. So this file keeps the state:
// `data-moving` on the element from the start of anything that moves, resizes
// or fades it — a transition, or keyframes that carry the switch — until none
// of it is left running, then `data-settling` for one beat of the tempo.
// `window.__echoSettle(el)` is that second half on its own, for a box a script
// stills by hand (the web's view transitions) — called *before* whatever held
// its shadows off comes down, for the reason `settle` gives.
//
// Shared by both clients for the reason `ink.js` is (D57): the desktop inlines
// it into the head of every window, the web imports it at the entry.
(function () {
  "use strict";

  if (typeof window === "undefined" || window.__echoMotion) return;

  // What `app.css` ships, for a page that has not got the stylesheet — a test
  // document, the feed harness. Never read in either client, which always has.
  var DEFAULT_MS = 233;

  function linear(p) {
    return p;
  }

  // A `cubic-bezier()` as a function of the time fraction. The curve is x(t)
  // for time and y(t) for progress over one parameter t, so the time fraction
  // is solved for t first — by halving, which cannot diverge: CSS keeps both x
  // in 0..1, so x(t) never turns back — and y is read at that t. Twenty halvings
  // is a millionth of the movement.
  function bezier(x1, y1, x2, y2) {
    function at(a, b, t) {
      var u = 1 - t;
      return 3 * a * u * u * t + 3 * b * u * t * t + t * t * t;
    }
    return function (p) {
      if (p <= 0) return 0;
      if (p >= 1) return 1;
      var lo = 0;
      var hi = 1;
      var t = p;
      for (var i = 0; i < 20; i++) {
        t = (lo + hi) / 2;
        if (at(x1, x2, t) < p) lo = t;
        else hi = t;
      }
      return at(y1, y2, t);
    };
  }

  window.__echoMotion = function (el) {
    var style = getComputedStyle(el);
    var ms = parseFloat(style.getPropertyValue("--motion-duration"));
    // `linear` and anything unreadable are the straight line: the only other
    // shape the stylesheet uses is `cubic-bezier()`.
    var curve = /cubic-bezier\(([^)]*)\)/.exec(style.getPropertyValue("--motion-easing"));
    var n = curve ? curve[1].split(",").map(Number) : [];
    var usable =
      n.length === 4 &&
      n.every(function (v) {
        return isFinite(v);
      });
    return {
      ms: isNaN(ms) ? DEFAULT_MS : Math.max(0, ms),
      ease: usable ? bezier(n[0], n[1], n[2], n[3]) : linear,
    };
  };

  // Two attributes, not classes: both clients' renderers own `class` and
  // `style` and rewrite them whole, and neither knows these are there.
  var MOVING = "data-moving";
  var SETTLING = "data-settling";

  // A transition that counts: one that makes a box travel, turn, grow or
  // fade. Not a colour, a `box-shadow` (the picker tiles transition their
  // rings, and every box hands its shadow back on one), or `visibility`, which
  // rides the stream blocks' slide on a zero-length transition of its own.
  var MOVES = /^(transform|translate|rotate|scale|opacity|filter|width|height)$/;

  // An animation that counts: keyframes that carry the switch
  // (`--motion-shadow`), which is every arrival and every departure and no
  // loop and no spotlight. Read out of the stylesheet itself, so that the list
  // is the stylesheet's alone and a new arrival counts the moment it carries
  // the switch — not off the animation, whose `getKeyframes()` leaves custom
  // properties out. One look answers every name the sheets declare; a name
  // first asked about afterwards, whose sheet came later, gets a look of its
  // own, and one that no sheet declares is remembered as no motion.
  var carriers = {};

  function read(rules) {
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (typeof CSSKeyframesRule !== "undefined" && rule instanceof CSSKeyframesRule) {
        var has = carriers[rule.name] === true;
        for (var j = 0; j < rule.cssRules.length && !has; j++) {
          has = rule.cssRules[j].style.getPropertyValue("--motion-shadow") !== "";
        }
        carriers[rule.name] = has;
      } else if (rule.cssRules) {
        read(rule.cssRules);
      }
    }
  }

  function carries(name) {
    if (!Object.prototype.hasOwnProperty.call(carriers, name)) {
      for (var i = 0; i < document.styleSheets.length; i++) {
        var rules = null;
        try {
          rules = document.styleSheets[i].cssRules;
        } catch (e) {
          // A sheet from another origin keeps its rules to itself.
        }
        if (rules) read(rules);
      }
      if (!Object.prototype.hasOwnProperty.call(carriers, name)) carriers[name] = false;
    }
    return carriers[name];
  }

  // An element's own motion only: one on a pseudo-element — a press fill on
  // `::before`, a sheet's title-bar glass, the web's view-transition tree —
  // moves nothing of the element's.
  function own(event) {
    return !event.pseudoElement && event.target instanceof Element;
  }

  // Whether anything that counts is still running on el. Its live list, so
  // that a box growing on two axes at once, a transition turned round
  // mid-flight (cancelled on the frame its successor starts) or an arrival
  // with a transition of its own still to go keep it moving to the last.
  //
  // A departure is never over: it holds its sunk end (`both`) until whoever
  // mounted it takes it away, and its dark goes with it. It must not settle
  // either, and not only because there is nothing to bring back — Chromium
  // starts a transition from the style *under* the animations, so a settling
  // box's shadow would come back through the fill that was holding it off.
  function busy(el) {
    var left = typeof el.getAnimations === "function" ? el.getAnimations() : [];
    for (var i = 0; i < left.length; i++) {
      var a = left[i];
      if (a.playState === "idle") continue;
      if (a.transitionProperty !== undefined) {
        if (a.playState !== "finished" && MOVES.test(a.transitionProperty)) return true;
      } else if (a.animationName !== undefined && carries(a.animationName)) {
        if (a.playState !== "finished") return true;
        var fill = a.effect ? a.effect.getComputedTiming().fill : "none";
        if (fill === "forwards" || fill === "both") return true;
      }
    }
    return false;
  }

  var timers = new WeakMap();

  function begin(el) {
    var timer = timers.get(el);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(el);
      el.removeAttribute(SETTLING);
    }
    if (!el.hasAttribute(MOVING)) el.setAttribute(MOVING, "");
  }

  function end(el) {
    if (el.hasAttribute(MOVING) && !busy(el)) settle(el);
  }

  // Off moving and into settling, for one beat of the tempo el inherits (none
  // while motion is off). Both writes land in one style change, or the switch
  // goes first and every shadow in el snaps back before its return has a
  // length — so the tempo is read *before* either attribute is touched: that
  // read is a style recalculation, and between the two writes it was exactly
  // the snap. The same holds for a caller of `__echoSettle`: settle first,
  // then take down whatever else was holding the shadows off. The attribute
  // outlives the change it was for only so that it cannot be gone before that
  // change is made; a running transition keeps its length whatever follows.
  function settle(el) {
    var ms = window.__echoMotion(el).ms;
    var timer = timers.get(el);
    if (timer !== undefined) clearTimeout(timer);
    timers.delete(el);
    el.removeAttribute(MOVING);
    if (ms <= 0) {
      el.removeAttribute(SETTLING);
      return;
    }
    el.setAttribute(SETTLING, "");
    timers.set(
      el,
      setTimeout(function () {
        timers.delete(el);
        el.removeAttribute(SETTLING);
      }, ms),
    );
  }
  window.__echoSettle = settle;

  // Capture, so that no handler below can stop any of it from being heard.
  function onTransition(event) {
    if (!own(event) || !MOVES.test(event.propertyName)) return;
    if (event.type === "transitionrun") begin(event.target);
    else end(event.target);
  }
  function onAnimation(event) {
    if (!own(event) || !carries(event.animationName)) return;
    if (event.type === "animationstart") begin(event.target);
    else end(event.target);
  }
  document.addEventListener("transitionrun", onTransition, true);
  document.addEventListener("transitionend", onTransition, true);
  document.addEventListener("transitioncancel", onTransition, true);
  document.addEventListener("animationstart", onAnimation, true);
  document.addEventListener("animationend", onAnimation, true);
  document.addEventListener("animationcancel", onAnimation, true);
})();
